
<svelte:options accessors={true} />

<script>
  import { createEventDispatcher, getContext } from 'svelte';
  import { writable } from 'svelte/store';
  import { fade } from 'svelte/transition';
  import Playlist from './playlist';

  const app = getContext('app');
  const devMode = app.key('devMode');
  const crossfade_disabled = app.feature('player.crossfade_disabled', false);

  const dispatch = createEventDispatcher();

  const CROSSFADE_DURATION = crossfade_disabled ? 0 : 200;
  const CROSSFADE_OFFSET = CROSSFADE_DURATION / 2;

  const FALLBACK_LIST = new Playlist([
    {
      id: '000',
      title: 'Fallback Content',
      mediaType: 'IMAGE',
      mediaUrl: new URL('/app/imd/content/player-fallback.png', location.href).href,
      durationSeconds: 10,
    }
  ], true);

  export let play;
  export let media_playlist;

  // Current and next playlist items
  const view = writable([]);

  export function currentView () { return $view; }

  let playlist_current = FALLBACK_LIST;
  let playlist_next;


  async function proceedNextItem () {

    const nextListLoaded = playlist_next && playlist_next._canPlay;

    // Check if there's new playlist available and do switch
    if (nextListLoaded) {
      console.log('Switch Player %s', playlist_next);
      playlist_current = playlist_next;
      playlist_next = null;
      dispatch('playlist.switch', playlist_current.toString());
    }

    try {
      if (playlist_current.paused) {
        $view = playlist_current.resume();
      }
      else {
        $view = playlist_current.proceedNextItem();
      }

      const { item } = ($view[1] || $view[0]);
      dispatch('media.play', item);
    } catch (e) {
      console.warn(e);
      $view = playlist_current.currentView();
      // Use fallback and make it stick until next update
      playlist_next = FALLBACK_LIST;
      $media_playlist = [];
      proceedNextItem();
    }
  }

  async function start() {
    await playlist_current.waitCanPlay();
    proceedNextItem();
  }

  function stop () {
    if (playlist_current) {
      $view = playlist_current.stop();
    }
  }

  // Playlist update
  $: if ($media_playlist) {
    if ($media_playlist.length) {
      const update = playlist_current.shouldUpdate($media_playlist);

      if (update && !playlist_next) {
        playlist_next = new Playlist($media_playlist);
        playlist_next.addLoadListener(({ event, data }) => {
          dispatch(event, data);
        });
        console.log('Update Player %s', playlist_next);
        dispatch('playlist.init', playlist_next.toString());
        playlist_next.waitFullyLoaded().then(() => dispatch('playlist.loaded'));
      }
    }
    else if (playlist_current != FALLBACK_LIST) {
      console.log('Update Player playlist to fallback');
      playlist_next = FALLBACK_LIST;
    }
  }

  $: {
    if (play) {
      start();
    }
    else {
      stop();
    }
  }

  function finished (media) {
    // Guard for media gone wild
    if (media.playing) {
      // console.log('finished', media.id);
      proceedNextItem();
    }
  }

  function mediaError (media) {
    media.error = true;
    // console.log('Error with', media.id);
    proceedNextItem();
    // tick();
  }

  function slotTitle (media) {
    const verb = media.playing ? 'Playing' : 'Waiting';
    return `${verb} -- ${media.item.id}`;
  }
</script>

<div class="ds-player" data-testid="ds-player">
  {#each $view as media (media.id)}
    <div class="player-slot" title={slotTitle(media)}
        transition:fade={{ duration: CROSSFADE_DURATION }}
        class:error={media.error}>
      <svelte:component this={media.component} item={media.item}
        playing={media.playing}
        crossfade_offset={CROSSFADE_OFFSET}
        on:media.ready="{() => media.ready = true}"
        on:media.finished="{() => finished(media)}"
        on:media.error="{()=>mediaError(media)}"
      />
      {#if devMode}
        <div class="item-id">
          <small>DevMode</small><br>
          {media.item.mediaType}: {media.item.id}
          <small>{media.item.title}</small>
        </div>
      {/if}
    </div>
  {/each}
</div>

<style media="screen">

  .ds-player {
    position: relative;
    width: 100%;
    height: 100%;
    background: #000;
    overflow: hidden;
  }

  .player-slot {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #000;

    pointer-events: none;
  }

  .item-id {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 8px 16px 4px;
    background-color: rgba(0,0,0,.5);
    color: #fff;
    line-height: 1.2;
  }
</style>
